import { useEffect, useRef, useState } from "react";

/**
 * Enkel stale-while-revalidate-cache for dashbord-widgets.
 *
 * - Viser cachet data UMIDDELBART (fra minne, evt. localStorage-snapshot), og
 *   revaliderer i bakgrunnen. Gir «instant» opplevelse ved fane-bytte og reload.
 * - Deler pågående hent mellom widgets (samme nøkkel → ett nettverkskall).
 *
 * Ingen TTL: vi henter alltid friskt i bakgrunnen, men bruker forrige svar som
 * plassholder. Tilstrekkelig for en intern portal. Byttes ev. til React Query
 * hvis behovene vokser.
 */

const mem = new Map<string, unknown>();
const inflight = new Map<string, Promise<unknown>>();

const pkey = (key: string) => `gb.cache.${key}`;

function lesSnapshot<T>(key: string): T | undefined {
  try {
    const raw = localStorage.getItem(pkey(key));
    if (!raw) return undefined;
    const parsed = JSON.parse(raw) as { data: T };
    return parsed?.data;
  } catch {
    return undefined;
  }
}

function skrivSnapshot<T>(key: string, data: T): void {
  try {
    localStorage.setItem(pkey(key), JSON.stringify({ data }));
  } catch {
    /* privat modus / full disk – snapshot er bare en bekvemmelighet */
  }
}

export type CachedResource<T> = {
  data: T | undefined;
  laster: boolean;
  feil: boolean;
};

export function useCachedResource<T>(
  key: string,
  fetcher: () => Promise<T>,
  opts?: { persist?: boolean }
): CachedResource<T> {
  const persist = opts?.persist ?? false;
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  const [data, setData] = useState<T | undefined>(
    () => (mem.get(key) as T | undefined) ?? (persist ? lesSnapshot<T>(key) : undefined)
  );
  const [feil, setFeil] = useState(false);

  useEffect(() => {
    let live = true;
    // Vis eventuell cache umiddelbart ved nøkkel-bytte
    const cached = (mem.get(key) as T | undefined) ?? (persist ? lesSnapshot<T>(key) : undefined);
    if (cached !== undefined) setData(cached);

    let p = inflight.get(key) as Promise<T> | undefined;
    if (!p) {
      p = fetcherRef
        .current()
        .then((d) => {
          mem.set(key, d);
          if (persist) skrivSnapshot(key, d);
          return d;
        })
        .finally(() => inflight.delete(key));
      inflight.set(key, p);
    }
    p.then((d) => {
      if (live) {
        setData(d);
        setFeil(false);
      }
    }).catch(() => {
      if (live && mem.get(key) === undefined) setFeil(true);
    });

    return () => {
      live = false;
    };
    // fetcher holdes i ref; effekten avhenger kun av nøkkelen
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, persist]);

  return { data, laster: data === undefined && !feil, feil };
}
